import { localStorage } from "@prismicio/editor-support/DOM"
import { z } from "zod"

import { env } from "../../app/env"

declare global {
  interface Window {
    analytics: Segment
    lintrk?: LinkedInConversionTracking
  }
}

interface Segment {
  load: (
    key: string,
    options?: { integrations: Record<string, boolean> },
  ) => void
  page: (options?: { contentGroup?: string }) => void
  track: (name: string, context: Record<string, unknown>) => void
}

interface LinkedInConversionTracking {
  (type: string, options?: { conversion_id?: number }): void
}

export const SEGMENT_DEMO_CONTENT_GROUP = "Interactive Demo"

const ucServiceSchema = z.object({
  id: z.string(),
  status: z.boolean(),
})
type UCService = z.infer<typeof ucServiceSchema>

const ucEventSchema = z.object({
  detail: z.object({
    action: z.union([
      z.literal("onAcceptAllServices"),
      z.literal("onUpdateServices"),
      z.literal("onDenyAllServices"),
    ]),
  }),
})

export function initUsercentrics() {
  const ucInteracted = getUcUserInteraction()
  if (ucInteracted) loadSegment()

  window.addEventListener("ucEvent", (rawEvent) => {
    const parsed = ucEventSchema.safeParse(rawEvent)
    if (!parsed.success) return

    const action = parsed.data.detail.action

    if (action === "onAcceptAllServices" || action === "onUpdateServices") {
      loadSegment()
    } else if (action === "onDenyAllServices") {
      // TODO: reset analytics
    }
  })
}

function loadSegment() {
  const ucSettings = getUcSettings()
  const services = aggregateServiceStatuses(ucSettings.services)

  window.analytics.load(env.PRISMIC_SEGMENT_WRITE_KEY, {
    integrations: {
      All: false,
      ...services,
    },
  })
  // TODO: Remove manual session ID generation once Segment handles it
  // automatically again.
  refreshSessionID(false)
  window.analytics.page({
    contentGroup: SEGMENT_DEMO_CONTENT_GROUP,
  })
}

function aggregateServiceStatuses(services: UCService[]) {
  const statuses = services.reduce<Record<string, boolean>>((acc, curr) => {
    acc[curr.id] = curr.status
    return acc
  }, {})

  function getStatus(id: string) {
    return statuses[id] ?? false
  }

  const result: Record<string, boolean> = {}

  const segment = getStatus("O97xcRJFR") // Segment.io Id from UC
  result["Segment.io"] = segment

  // const youtube = getStatus("BJz7qNsdj-7") // Youtube Video Id from UC
  // result["Youtube Video"] = youtube
  result["Youtube Video"] = false // Disable YouTube in the demo

  const hubspot = segment && getStatus("ry0QcNodoWQ") // Hubspot Web Id from UC
  result["Hubspot Web (Actions)"] = hubspot

  const hotjar = segment && getStatus("S1kgcNo_j-m") // Hotjar Id from UC
  result["Hotjar"] = hotjar

  const amplitude = segment && getStatus("Sk9kb5VoOi-7") // Amplitude Actions Id from UC
  result["Amplitude (Actions)"] = amplitude

  const facebookPixel =
    segment &&
    getStatus("ko1w5PpFl") && // Facebook Pixel Id from UC
    getStatus("XYQZBUojc") // Facebook Social Plugins Id from UC
  result["Facebook Pixel"] = facebookPixel

  const linkedInInsightTag =
    segment &&
    getStatus("Hkx754i_iWm") && // LinkedIn Plugin Id from UC
    getStatus("rk-nqEj_o-m") && // LinkedIn Ads Id from UC
    getStatus("JQ2XQxIk") // LinkedIn Insight Tag Id from UC
  result["LinkedIn Insight Tag"] = linkedInInsightTag

  const googleAdwordsNew =
    segment &&
    getStatus("rJ99c4oOo-X") && // Google AdServices Id from UC
    getStatus("S1_9Vsuj-Q") && // Google Ads Id from UC
    getStatus("SkPc5EjOsWm") && // Google Syndication Id from UC
    getStatus("BJ59EidsWQ") && // Google Tag Manager Id from UC
    getStatus("9V8bg4D63") // DoubleClick Ad Id from UC
  result["Google AdWords New"] = googleAdwordsNew

  return result
}

function getUcUserInteraction() {
  return localStorage.readJSON("uc_user_interaction", z.boolean())
}

function getUcSettings() {
  const validator = z.object({
    services: z.array(ucServiceSchema),
  })

  return localStorage.readJSON("uc_settings", validator) ?? { services: [] }
}

export function getAnalyticsSessionId() {
  return localStorage.read("analytics_session_id")
}

// Segment stopped generating the session ID as of 2025-07-08. We are not
// sure why, but we can see Segment's Amplitude Session Plugin is no longer
// running. This code replicates what Session Plugin does.
// See: https://github.com/segmentio/action-destinations/blob/12255568e4a6d35cf05ee79a118ee6c1a6823f31/packages/browser-destinations/destinations/amplitude-plugins/src/sessionId/index.ts#L47-L84
// TODO: Remove manual session ID generation once Segment handles it
// automatically again.
export function refreshSessionID(updateLastAccess = true) {
  const newSession = Date.now().toString()
  if (!localStorage.has("analytics_session_id")) {
    localStorage.write("analytics_session_id", newSession)
  }

  if (
    !localStorage.has("analytics_session_id.last_access") ||
    updateLastAccess
  ) {
    localStorage.write("analytics_session_id.last_access", newSession)
  }
}
